"use client";

import DynamicComponent from "@/components/DynamicComponentLoader";
import { FloatingWhatsAppButton } from "@/components/FloatingWhatsAppButton";
import { useConfig } from "@/context/ConfigContext";
import { useProductCategories } from "@/hooks/useProductCategories";
import {
  getBuilderModeFromLocation,
  handleBuilderComponentClick,
} from "@/lib/builderBridge";
import { getSortedComponents } from "@/utils/componentMapper";
import dynamic from "next/dynamic";
import { useMemo } from "react";

const Header = dynamic(
  () => import("@/components/Header").then((mod) => mod.Header),
  { ssr: false }
);
const Footer = dynamic(
  () => import("@/components/Footer").then((mod) => mod.Footer),
  { ssr: false }
);

const useHeaderFooterData = ({
  imageBaseUrl = "",
  categories: providedCategories = null,
  categoriesLoading: providedCategoriesLoading = false,
  categoriesError: providedCategoriesError = null,
}) => {
  const { configData } = useConfig();

  const {
    categories: fetchedCategories,
    loading: fetchedCategoriesLoading,
    error: fetchedCategoriesError,
  } = useProductCategories();

  const categories = providedCategories ?? fetchedCategories;
  const categoriesLoading =
    providedCategoriesLoading || fetchedCategoriesLoading;
  const categoriesError = providedCategoriesError || fetchedCategoriesError;

  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  const headerComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "header");
  }, [sortedComponents]);

  const footerComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "footer");
  }, [sortedComponents]);

  const finalImageBaseUrl =
    imageBaseUrl || configData?.config?.api?.imageUrl || "";

  return {
    headerComponent,
    footerComponent,
    categories,
    categoriesLoading,
    categoriesError,
    finalImageBaseUrl,
  };
};

export function DynamicHeader({
  headerProps = {},
  imageBaseUrl = "",
  categories = null,
  categoriesLoading = false,
  categoriesError = null,
}) {
  const isBuilderMode = getBuilderModeFromLocation();
  const {
    headerComponent,
    categories: resolvedCategories,
    categoriesLoading: resolvedCategoriesLoading,
    finalImageBaseUrl,
  } = useHeaderFooterData({
    imageBaseUrl,
    categories,
    categoriesLoading,
    categoriesError,
  });

  if (headerComponent) {
    const headerComponentId = Number(headerComponent.id);
    const headerNode = (
      <DynamicComponent
        componentConfig={headerComponent}
        imageBaseUrl={finalImageBaseUrl}
        categories={resolvedCategories}
        loading={resolvedCategoriesLoading}
        {...headerProps}
      />
    );
    if (!isBuilderMode || !Number.isFinite(headerComponentId))
      return headerNode;
    return (
      <div
        data-builder-id={headerComponentId}
        data-builder-type="header"
        data-builder-label={`header #${headerComponentId}`}
        onClickCapture={(event) => handleBuilderComponentClick(event, headerComponentId)}
      >
        {headerNode}
      </div>
    );
  }

  return (
    <Header
      categories={resolvedCategories}
      loading={resolvedCategoriesLoading}
      {...headerProps}
    />
  );
}

export function DynamicFooter({
  footerProps = {},
  imageBaseUrl = "",
  categories = null,
  categoriesLoading = false,
  categoriesError = null,
}) {
  const isBuilderMode = getBuilderModeFromLocation();
  const { footerComponent, finalImageBaseUrl } = useHeaderFooterData({
    imageBaseUrl,
    categories,
    categoriesLoading,
    categoriesError,
  });

  if (footerComponent) {
    const footerComponentId = Number(footerComponent.id);
    const footerNode = (
      <DynamicComponent
        componentConfig={footerComponent}
        imageBaseUrl={finalImageBaseUrl}
        {...footerProps}
      />
    );
    if (!isBuilderMode || !Number.isFinite(footerComponentId)) {
      return (
        <>
          {footerNode}
          <FloatingWhatsAppButton />
        </>
      );
    }
    return (
      <>
        <div
          data-builder-id={footerComponentId}
          data-builder-type="footer"
          data-builder-label={`footer #${footerComponentId}`}
          onClickCapture={(event) => handleBuilderComponentClick(event, footerComponentId)}
        >
          {footerNode}
        </div>
        <FloatingWhatsAppButton />
      </>
    );
  }

  return <Footer {...footerProps} />;
}
